import { isErrorCode, type ValidationIssue } from '@wasity/shared';
import { ApiError } from './api';

type T = (key: string, vars?: Record<string, string | number>) => string;

/**
 * The code out of anything a mutation or query can reject with: an `ApiError`, a plain object carrying
 * `code` (TanStack Query hands the error back untyped), or the bare code string several screens keep in
 * their own state. Empty when there is no code to read.
 */
export function errorCodeOf(e: unknown): string {
  if (typeof e === 'string') return e;
  if (e instanceof ApiError) return e.code;
  if (e && typeof e === 'object' && 'code' in e) {
    const c = (e as { code: unknown }).code;
    if (typeof c === 'string') return c;
  }
  return '';
}

/**
 * One sentence for a failed action, in the reader's language.
 *
 * Every code the API documents as worth wording has an entry under `errors.*` in ar.json and en.json
 * (the contract is `packages/shared/src/errors.ts`; the list is in docs/api-errors.md). Anything else
 * falls back to the generic line — with the raw code appended only in a dev build, because
 * «تعذّر تنفيذ الإجراء (payment_wrong_request)» is an English token inside an Arabic sentence and means
 * nothing to the person reading it.
 */
export function errorText(e: unknown, t: T): string {
  // A sentence a screen already worded (a partial photo upload, say) is shown as it is. A code never has a space in it.
  if (typeof e === 'string' && /\s/.test(e)) return e;
  const issues = validationIssues(e);
  if (issues?.length) return `${t('errors.validation_failed')} ${issues.map((i) => issueLine(i, t)).join(' · ')}`;
  return specificErrorText(e, t) ?? genericText(t, errorCodeOf(e), import.meta.env.DEV);
}

/**
 * What a 422 said field by field (`details.issues`, docs/api-errors.md). Every screen used to throw this
 * away and print «check the marked fields» over a form where nothing was marked.
 */
export function validationIssues(e: unknown): ValidationIssue[] | null {
  if (!(e instanceof ApiError) || e.code !== 'validation_failed') return null;
  const issues = (e.details as { issues?: unknown } | undefined)?.issues;
  return Array.isArray(issues) ? (issues as ValidationIssue[]).filter((i) => i && typeof i.path === 'string') : null;
}

/** The broken rule in the reader's language. zod's English `message` is never shown. */
export function ruleText(i: Pick<ValidationIssue, 'rule'>, t: T): string {
  const r = i.rule;
  if (!r || r.kind === 'invalid') return t('admin.validation.invalid');
  if (r.kind === 'required') return t('admin.validation.required');
  if (r.n == null) return t('admin.validation.invalid');
  return t(`admin.validation.rule.${r.kind}`, { n: r.n });
}

/**
 * The label a reader knows a field by. The path is the request body's (`property.areaM2`,
 * `slots.2.start`), so the full path is tried first and then its last named part; a field with no
 * entry in `fields.*` is left unnamed rather than shown as a code.
 */
export function fieldLabel(path: string, t: T): string | null {
  const named = path.split('.').filter((s) => s && !/^\d+$/.test(s));
  for (const key of [named.join('.'), named[named.length - 1]]) {
    if (!key) continue;
    const s = t(`fields.${key}`);
    if (s !== `fields.${key}`) return s;
  }
  return null;
}

const issueLine = (i: ValidationIssue, t: T) => {
  const label = fieldLabel(i.path, t);
  return label ? `${label} — ${ruleText(i, t)}` : ruleText(i, t);
};

/**
 * The same issues keyed by the form's own field names, for a form that marks its fields. `map` turns a
 * request path into the form key when the two differ (`listing.priceHalalas` → `price`); a path it
 * returns nothing for is matched on its last named part.
 */
export function fieldErrors(e: unknown, t: T, map: (path: string) => string | undefined = () => undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const i of validationIssues(e) ?? []) {
    const key = map(i.path) ?? i.path.split('.').filter((s) => s && !/^\d+$/.test(s)).pop();
    if (key && !out[key]) out[key] = ruleText(i, t);
  }
  return out;
}

/**
 * The line for a code the product does not word. In a dev build the raw code rides along, because that
 * is the whole of the signal when something new appears; in a shipped build it never does. Exported so
 * both branches can be asserted — under vitest `import.meta.env.DEV` is true, so a test that called
 * `errorText` alone would only ever see the dev half.
 */
export function genericText(t: T, code: string, dev: boolean): string {
  return dev && code ? `${t('app.unknownError')} (${code})` : t('app.unknownError');
}

/** The sentence for this code, or null when there is none — for callers that have their own generic line. */
export function specificErrorText(e: unknown, t: T): string | null {
  const code = errorCodeOf(e);
  if (!isErrorCode(code)) return null;
  const key = `errors.${code}`;
  const s = t(key);
  return s === key ? null : s;
}

/** How long to wait before retrying, when the API said so (429). */
export const retryAfterOf = (e: unknown): number | undefined => (e instanceof ApiError ? e.retryAfter : undefined);
